/**
 * Secura Web : colonne gauche.
 * — Liste projets (Assignés / Créés) comme mobile + statut, % et compteurs tâches
 * — Au clic projet : conversation à droite + bloc tâches (A faire / En cours / Faite)
 * — Cercle bleu retour → liste projets
 */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, Plus, Circle, CheckCircle, Clock, AlertCircle, Info, X } from 'lucide-react';
import { FaChevronLeft } from 'react-icons/fa';
import axiosInstance from '../../config/axios';
import { AppContext } from '../../contexts/AppContext';
import { useSocketOptional } from '../../contexts/SocketContext';
import { WEB_SIDEBAR_WIDTH, WEB_NAVBAR_HEIGHT } from '../../utils/webHost';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { assets } from '../../assets/assets';
import { getPhotoUrl } from '../../utils/imageUtils';
import { cn } from '@/lib/utils';
import WebProfileHeader from './WebProfileHeader';

const TASK_FILTERS = [
  { key: 'todo', label: 'A faire', icon: Circle },
  { key: 'en_cours', label: 'En cours', icon: Loader2 },
  { key: 'termine', label: 'Faite', icon: CheckCircle },
];

const STATUS_LABEL = {
  planifie: 'Planifié',
  en_cours: 'En cours',
  en_pause: 'En pause',
  termine: 'Terminé',
  annule: 'Annulé',
};

const fold = (v) => String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

function memberPhoto(m) {
  const raw = m?.profileImageUrl;
  if (!raw) return null;
  return getPhotoUrl(raw);
}

/** Avatars liste projet : ≤3 tous affichés ; >3 → 2 photos + cercle bleu +(n-2). */
function ProjectAvatars({ members = [] }) {
  const list = members.length ? members : [{ id: 'fallback', nom_complet: '?', profileImageUrl: null }];
  const n = list.length;
  const showPlus = n > 3;
  const shown = showPlus ? list.slice(0, 2) : list.slice(0, 3);
  const extra = showPlus ? n - 2 : 0;

  return (
    <div className="flex -space-x-3 shrink-0 items-center">
      {shown.map((m) => {
        const src = m.id === 'fallback' ? null : memberPhoto(m);
        return (
          <Avatar key={m.id} className="h-10 w-10">
            {src ? (
              <AvatarImage src={src} alt="" className="object-cover" />
            ) : (
              <AvatarImage src={assets.employes} alt="" className="object-cover" />
            )}
            <AvatarFallback className="bg-transparent p-0">
              <img src={assets.employes} alt="" className="h-full w-full object-cover" />
            </AvatarFallback>
          </Avatar>
        );
      })}
      {extra > 0 ? (
        <div className="h-10 w-10 rounded-full bg-[#0026E2] text-white text-xs font-bold flex items-center justify-center ring-2 ring-white z-10">
          +{extra}
        </div>
      ) : null}
    </div>
  );
}

/**
 * En-tête projet ouvert : retour + photo.
 * 2 membres → badge employé ; >2 → badge bleu +(n-1).
 */
function ProjectOpenHeader({ projet, onBack, spinning }) {
  const members = projet?.members || [];
  const n = members.length;
  const first = members[0];
  const photoSrc = first ? memberPhoto(first) : null;
  const showPlusBadge = n > 2;
  const plusCount = n > 2 ? n - 1 : 0;

  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200 shrink-0">
      <button
        type="button"
        onClick={onBack}
        className={cn(
          'w-9 h-9 shrink-0 rounded-full bg-[#0026E2] flex items-center justify-center shadow-md cursor-pointer transition-transform duration-500 hover:bg-[#0019B3]',
          spinning ? 'scale-110 rotate-[-360deg]' : 'hover:scale-105'
        )}
        title="Retour aux projets"
        aria-label="Retour aux projets"
      >
        <FaChevronLeft className="text-white text-sm" />
      </button>
      <div className="relative h-12 w-12 shrink-0">
        <div className="h-12 w-12 rounded-full overflow-hidden">
          <img src={photoSrc || assets.employes} alt="" className="h-full w-full object-cover" />
        </div>
        {n >= 2 ? (
          showPlusBadge ? (
            <div className="absolute -bottom-0.5 -right-0.5 h-7 w-7 rounded-full bg-[#0026E2] text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
              +{plusCount}
            </div>
          ) : (
            <div className="absolute -bottom-0.5 -right-0.5 h-7 w-7 rounded-full overflow-hidden ring-2 ring-white bg-[#c5d4f5]">
              <img
                src={memberPhoto(members[1]) || assets.employes}
                alt=""
                className="h-full w-full object-cover"
              />
            </div>
          )
        ) : null}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-[#0B3A4A]">{projet?.nom || 'Projet'}</p>
        <p className="truncate text-xs text-gray-500">
          {(projet?.description && String(projet.description).trim()) || 'Tâches du projet'}
        </p>
      </div>
    </div>
  );
}

/** Modale détails projet (icône info). */
function ProjectDetailsModal({ projet, onClose }) {
  if (!projet) return null;
  const stats = projet.taskStats || {};
  const pct = typeof projet.progress === 'number' ? projet.progress : 0;
  const members = projet.members || [];

  return createPortal(
    <div className="fixed inset-0 z-[10000] bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Détails du projet"
        className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-2 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900 truncate pr-2">{projet.nom}</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center shrink-0"
            aria-label="Fermer"
          >
            <X size={18} className="text-gray-500" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Description</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {(projet.description && String(projet.description).trim()) || 'Aucune description'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: '#0026E2' }}>
              {STATUS_LABEL[projet.statut] || projet.statut || 'Planifié'}
            </span>
            <span className="text-sm font-bold text-[#0026E2]">{pct}%</span>
          </div>
          <div>
            <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${pct}%`, backgroundColor: pct === 100 ? '#15d415' : '#0026E2' }}
              />
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-600">
            <span>{stats.todo || 0} à faire</span>
            <span style={{ color: '#0026E2' }}>{stats.en_cours || 0} en cours</span>
            <span className="text-green-600">{stats.termine || 0} fait</span>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase mb-2">
              Membres ({members.length})
            </p>
            <ul className="space-y-2">
              {members.length ? (
                members.map((m) => (
                  <li key={m.id} className="flex items-center gap-2">
                    <img
                      src={memberPhoto(m) || assets.employes}
                      alt=""
                      className="h-8 w-8 rounded-full object-cover"
                    />
                    <span className="text-sm text-gray-800 truncate">{m.nom_complet || 'Membre'}</span>
                  </li>
                ))
              ) : (
                <li className="text-sm text-gray-500">Aucun membre</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function WebProjectsSidebar({ menuOffset = 0, embedded = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { socket } = useSocketOptional();
  const { userData, interimStatus } = useContext(AppContext);
  const canCreate = !!(userData?.isResponsableDepartement || interimStatus?.isInterim);
  const [projets, setProjets] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [projectFilter, setProjectFilter] = useState('assigned'); // assigned | created
  const [taskFilter, setTaskFilter] = useState('todo');
  const [cursor, setCursor] = useState(-1);
  const [view, setView] = useState('projects'); // 'projects' | 'tasks'
  const [spinningBack, setSpinningBack] = useState(false);
  const [detailsProjet, setDetailsProjet] = useState(null);
  const refreshTimer = useRef(null);
  const listRef = useRef(null);

  const activeId = Number((/^\/projets\/(\d+)/.exec(location.pathname) || [])[1]) || null;
  const readingId =
    activeId && !location.pathname.includes('/tasks') && new URLSearchParams(location.search).get('tab') === 'conversation'
      ? activeId
      : null;

  const activeProjet = useMemo(() => projets.find((p) => p.id === activeId) || null, [projets, activeId]);

  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/projets/sidebar');
      setProjets(Array.isArray(data?.data) ? data.data : []);
    } catch {
      // liste inchangée
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTasks = useCallback(async (projetId) => {
    if (!projetId) return;
    setTasksLoading(true);
    try {
      const { data } = await axiosInstance.get(`/tasks?projet_id=${projetId}`);
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setTasks(list);
    } catch {
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (activeId) {
      setView('tasks');
      setTaskFilter('todo');
      loadTasks(activeId);
    } else {
      setView('projects');
      setTasks([]);
    }
  }, [activeId, loadTasks]);

  useEffect(() => {
    const onSearch = (e) => setQuery(String(e.detail ?? ''));
    window.addEventListener('secura-web-project-search', onSearch);
    return () => window.removeEventListener('secura-web-project-search', onSearch);
  }, []);

  useEffect(() => {
    if (!socket) return undefined;
    const refresh = () => {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(() => {
        load();
        if (activeId) loadTasks(activeId);
      }, 1500);
    };
    socket.on('badge:update', refresh);
    return () => {
      socket.off('badge:update', refresh);
      clearTimeout(refreshTimer.current);
    };
  }, [socket, load, loadTasks, activeId]);

  useEffect(() => {
    if (!readingId) return;
    setProjets((list) =>
      list.some((p) => p.id === readingId && p.unreadCount)
        ? list.map((p) => (p.id === readingId ? { ...p, unreadCount: 0 } : p))
        : list
    );
  }, [readingId, projets]);

  const uid = Number(userData?.id);

  const shownProjects = useMemo(() => {
    let list = projets;
    if (projectFilter === 'created') {
      list = list.filter((p) => Number(p.responsable_id) === uid);
    } else {
      // Assignés : membre du projet (liste sidebar = assignés + responsables)
      list = list.filter((p) =>
        (p.members || []).some((m) => Number(m.id) === uid) || Number(p.responsable_id) === uid
      );
    }
    const q = fold(query.trim());
    if (q) {
      list = list.filter((p) => fold(p.nom).includes(q) || fold(p.description).includes(q));
    }
    return list;
  }, [projets, query, projectFilter, uid]);

  const projectCounts = useMemo(
    () => ({
      assigned: projets.filter(
        (p) => (p.members || []).some((m) => Number(m.id) === uid) || Number(p.responsable_id) === uid
      ).length,
      created: projets.filter((p) => Number(p.responsable_id) === uid).length,
    }),
    [projets, uid]
  );

  const projectTabFilters = useMemo(() => {
    const tabs = [{ key: 'assigned', label: 'Assignés' }];
    if (canCreate) tabs.push({ key: 'created', label: 'Créés' });
    return tabs;
  }, [canCreate]);

  useEffect(() => {
    if (!canCreate && projectFilter === 'created') setProjectFilter('assigned');
  }, [canCreate, projectFilter]);

  const shownTasks = useMemo(() => {
    return tasks.filter((t) => t.statut === taskFilter);
  }, [tasks, taskFilter]);

  const taskCounts = useMemo(
    () => ({
      todo: tasks.filter((t) => t.statut === 'todo').length,
      en_cours: tasks.filter((t) => t.statut === 'en_cours').length,
      termine: tasks.filter((t) => t.statut === 'termine').length,
    }),
    [tasks]
  );

  const open = useCallback(
    (p) => {
      setView('tasks');
      setTaskFilter('todo');
      navigate(`/projets/${p.id}?tab=conversation`);
    },
    [navigate]
  );

  const backToProjects = useCallback(() => {
    if (spinningBack) return;
    setSpinningBack(true);
    setTimeout(() => {
      setView('projects');
      setSpinningBack(false);
      navigate('/projets');
    }, 480);
  }, [navigate, spinningBack]);

  useEffect(() => setCursor(query ? 0 : -1), [query]);

  useEffect(() => {
    const onKey = (e) => {
      if (view !== 'projects') return;
      if (e.altKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp') && shownProjects.length) {
        e.preventDefault();
        const i = shownProjects.findIndex((p) => p.id === activeId);
        const next =
          e.key === 'ArrowDown'
            ? Math.min(shownProjects.length - 1, i + 1)
            : Math.max(0, i === -1 ? 0 : i - 1);
        open(shownProjects[next]);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [shownProjects, activeId, open, view]);

  useEffect(() => {
    if (cursor < 0) return;
    listRef.current?.querySelector(`[data-row="${cursor}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  return (
    <aside
      className={
        embedded
          ? 'hidden lg:flex flex-col bg-white border-r border-gray-200 shrink-0 h-full'
          : 'hidden lg:flex fixed bottom-0 flex-col bg-white border-r border-gray-200 z-30'
      }
      style={
        embedded
          ? { width: WEB_SIDEBAR_WIDTH }
          : { top: WEB_NAVBAR_HEIGHT, left: menuOffset, width: WEB_SIDEBAR_WIDTH }
      }
      aria-label={view === 'tasks' ? 'Tâches du projet' : 'Mes projets'}
    >
      {view === 'tasks' && activeProjet ? (
        <ProjectOpenHeader projet={activeProjet} onBack={backToProjects} spinning={spinningBack} />
      ) : (
        <WebProfileHeader />
      )}

      {/* Onglets toujours visibles : projets OU A faire / En cours / Faite */}
      <div className="px-3 pt-2 bg-white border-b border-gray-100 shrink-0">
        <div
          className="flex items-stretch justify-between gap-1"
          role="tablist"
          aria-label={view === 'tasks' ? 'Filtrer les tâches' : 'Filtrer les projets'}
        >
          {(view === 'tasks' ? TASK_FILTERS : projectTabFilters).map((f) => {
            const on =
              view === 'tasks'
                ? f.key === taskFilter
                : f.key === projectFilter && !query;
            const Icon = f.icon;
            const count = view === 'tasks' ? taskCounts[f.key] : projectCounts[f.key];
            return (
              <button
                key={f.key}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => {
                  if (view === 'tasks') setTaskFilter(f.key);
                  else {
                    setQuery('');
                    setProjectFilter(f.key);
                  }
                }}
                className={cn(
                  'flex-1 flex flex-col items-center gap-1 pb-2.5 pt-1.5 text-xs font-bold transition-colors border-b-[3px] rounded-t-lg cursor-pointer',
                  on
                    ? 'text-[#0026E2] border-[#0026E2] bg-white'
                    : 'text-gray-400 border-transparent hover:text-gray-600'
                )}
              >
                {Icon ? <Icon size={18} /> : null}
                <span>
                  {f.label}
                  {count ? <span className="ml-1 font-semibold opacity-70">({count})</span> : null}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto relative">
        {view === 'tasks' ? (
          tasksLoading ? (
            <div className="p-4 space-y-4" aria-busy="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-14 rounded-xl bg-gray-200 animate-pulse" />
              ))}
            </div>
          ) : shownTasks.length ? (
            <ul className="py-2">
              {shownTasks.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/projets/${activeId}/tasks?filter=${taskFilter}`)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors cursor-pointer hover:bg-white/80"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-bold text-[#0B3A4A] truncate">{t.titre}</span>
                      {t.description ? (
                        <span className="block text-xs text-gray-500 truncate mt-0.5">{t.description}</span>
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 text-center px-6 py-10">
              {taskFilter === 'en_cours'
                ? 'Aucune tâche en cours.'
                : taskFilter === 'termine'
                  ? 'Aucune tâche faite.'
                  : 'Aucune tâche à faire.'}
            </p>
          )
        ) : loading ? (
          <div className="p-4 space-y-4" aria-busy="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-2/3 rounded bg-gray-200 animate-pulse" />
                  <div className="h-3 w-5/6 rounded bg-gray-200 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : !shownProjects.length ? (
          <p className="text-sm text-gray-500 text-center px-6 py-10">
            {query
              ? 'Aucun projet trouvé.'
              : projectFilter === 'created'
                ? 'Aucun projet créé.'
                : 'Aucun projet assigné.'}
          </p>
        ) : (
          <ul className="py-2 px-3 space-y-2">
            {shownProjects.map((p, i) => {
              const active = p.id === activeId;
              const highlighted = i === cursor;
              const stats = p.taskStats || {};
              const tasksTodo = stats.todo || 0;
              const tasksEnCours = stats.en_cours || 0;
              const tasksTerminees = stats.termine || 0;
              const totalTasks = stats.total || 0;
              const pct = typeof p.progress === 'number' ? p.progress : totalTasks ? Math.round((tasksTerminees / totalTasks) * 100) : 0;
              const isComplete = pct === 100;
              const barColor = isComplete ? '#15d415' : '#0026E2';
              const statusLabel = STATUS_LABEL[p.statut] || p.statut || 'Planifié';

              return (
                <li key={p.id}>
                  <button
                    data-row={i}
                    type="button"
                    onClick={() => open(p)}
                    className={cn(
                      'w-full text-left rounded-xl border p-3 transition-colors cursor-pointer bg-white',
                      active ? 'border-[#0026E2] shadow-sm' : highlighted ? 'border-gray-300' : 'border-gray-200 hover:border-gray-300'
                    )}
                    aria-current={active ? 'page' : undefined}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <span className="block text-sm font-bold text-[#0B3A4A] truncate">{p.nom}</span>
                        {p.description ? (
                          <span className="block text-xs text-gray-500 truncate mt-0.5">{p.description}</span>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDetailsProjet(p);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.stopPropagation();
                              setDetailsProjet(p);
                            }
                          }}
                          className="p-1 rounded-full hover:bg-blue-50 cursor-pointer"
                          style={{ color: '#0026E2' }}
                          title="Informations"
                          aria-label="Informations"
                        >
                          <Info size={16} />
                        </span>
                        <span
                          className="px-2 py-0.5 rounded-full text-[10px] font-semibold text-white"
                          style={{ backgroundColor: '#0026E2' }}
                        >
                          {statusLabel}
                        </span>
                        <span className="text-[11px] font-bold text-[#0026E2] tabular-nums">{pct}%</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mb-2">
                      <ProjectAvatars members={p.members || []} />
                      {/* Trait de progression : se remplit selon le % (comme mobile) */}
                      <div className="relative flex-1 h-2.5 bg-gray-200 rounded-full overflow-hidden min-w-0">
                        <div
                          className="h-full transition-all duration-300 rounded-full"
                          style={{ width: `${Math.min(100, Math.max(0, pct))}%`, backgroundColor: barColor }}
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-3 text-[11px]">
                      {tasksTodo > 0 ? (
                        <span className="flex items-center gap-1 text-gray-600">
                          <AlertCircle size={12} className="text-gray-500" />
                          {tasksTodo} à faire
                        </span>
                      ) : null}
                      {tasksEnCours > 0 ? (
                        <span className="flex items-center gap-1" style={{ color: '#0026E2' }}>
                          <Clock size={12} />
                          {tasksEnCours} en cours
                        </span>
                      ) : null}
                      {tasksTerminees > 0 ? (
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle size={12} />
                          {tasksTerminees} fait
                        </span>
                      ) : null}
                      {totalTasks === 0 ? <span className="text-gray-400">Aucune tâche</span> : null}
                      {(p.unreadCount || 0) > 0 ? (
                        <span className="ml-auto min-w-[18px] h-4 px-1 rounded-full bg-[#0026E2] text-white text-[10px] font-bold flex items-center justify-center">
                          {p.unreadCount > 99 ? '99+' : p.unreadCount}
                        </span>
                      ) : null}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {canCreate && view === 'projects' ? (
          <button
            type="button"
            onClick={() => navigate('/projets/create')}
            className="absolute bottom-5 right-5 w-12 h-12 rounded-full bg-[#0026E2] hover:bg-[#0019B3] text-white shadow-lg flex items-center justify-center cursor-pointer"
            title="Nouveau projet"
            aria-label="Nouveau projet"
          >
            <Plus size={22} />
          </button>
        ) : null}
      </div>

      {detailsProjet ? (
        <ProjectDetailsModal projet={detailsProjet} onClose={() => setDetailsProjet(null)} />
      ) : null}
    </aside>
  );
}
